import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import logoImage from "@assets/favicon new no background cc6633.png";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { queryClient, getCsrfHeaders } from "@/lib/queryClient";
import { useCsrfToken } from "@/hooks/use-csrf-token";
import { Loader2, ShieldAlert, Mail } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Link } from "wouter";

export default function Login() {
  useCsrfToken();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [suspendedInfo, setSuspendedInfo] = useState<{ suspended: boolean; reason?: string } | null>(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("suspended") === "true") {
      return { suspended: true, reason: params.get("reason") || undefined };
    }
    return null;
  });

  const [resendEmailSent, setResendEmailSent] = useState(false);

  const resendMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch("/api/auth/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
        body: JSON.stringify({ email }),
        credentials: "include",
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to resend verification email");
      }
      return response.json();
    },
    onSuccess: () => {
      setResendEmailSent(true);
    },
    onError: (error: Error) => {
      toast({
        title: "Could not resend email",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const loginMutation = useMutation({
    mutationFn: async (data: { email: string; password: string }) => {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
        body: JSON.stringify(data),
        credentials: 'include',
      });
      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 403 && errorData.suspended) {
          setSuspendedInfo({ suspended: true, reason: errorData.reason });
          throw new Error(errorData.error || "Account suspended");
        }
        throw new Error(errorData.error || "Login failed");
      }
      return response.json();
    },
    onSuccess: async (data) => {
      try {
        queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
        // Session is regenerated on login, so force a fresh CSRF token fetch
        // for the new session before any state mutations can run.
        queryClient.invalidateQueries({ queryKey: ["/api/csrf-token"] });

        // Consume partner token if present
        const params = new URLSearchParams(window.location.search);
        const partnerToken = params.get("partnerToken");
        if (partnerToken) {
          try {
            const response = await fetch("/api/partner-invites/accept-token", {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
              body: JSON.stringify({ token: partnerToken }),
            });
            const tokenData = await response.json();
            if (tokenData.success && tokenData.loadId) {
              window.location.replace(`/find-loads?highlight=${tokenData.loadId}`);
              return;
            }
          } catch (err) {
            console.error("Failed to consume partner token:", err);
            // Non-fatal — continue to normal post-login flow
          }
        }

        // Handle returnTo redirect for users who were sent to login from a protected page
        const returnTo = new URLSearchParams(window.location.search).get("returnTo");
        if (returnTo && returnTo.startsWith("/") && !partnerToken) {
          window.location.replace(returnTo);
          return;
        }

        // Check user profile status to redirect appropriately
        const userResponse = await fetch("/api/auth/user", { credentials: 'include' });
        if (userResponse.ok) {
          const userData = await userResponse.json();
          const profileCompleted = userData.profileCompleted === "true" || userData.profileCompleted === true;
          const isAdmin = userData.isAdmin === "true" || userData.isAdmin === true;

          if (isAdmin) {
            window.location.replace("/admin");
          } else if (profileCompleted) {
            window.location.replace("/dashboard");
          } else {
            window.location.replace("/select-role");
          }
        } else {
          window.location.replace("/select-role");
        }
      } catch (error) {
        console.error("Login redirect error:", error);
        window.location.replace("/dashboard");
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Login Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loginMutation.mutate({ email, password });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/30 to-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-6">
          <Link href="/" className="flex justify-center" data-testid="link-login-logo">
            <img src={logoImage} alt="Fill The Trucks" className="h-24" />
          </Link>
          <div>
            <CardTitle className="text-3xl font-bold tracking-wide mb-2">
              Welcome Back
            </CardTitle>
            <CardDescription className="text-base">
              Sign in to Fill The Trucks to connect with carriers, shippers, and brokers
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {suspendedInfo?.suspended && (() => {
            const isEmailSuspension = /email|verified/i.test(suspendedInfo.reason || "");
            if (isEmailSuspension) {
              return (
                <Alert
                  variant="default"
                  className="border-amber-400 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-500"
                  data-testid="alert-email-verification-required"
                >
                  <Mail className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <AlertTitle className="text-amber-800 dark:text-amber-300">
                    Please verify your email to continue
                  </AlertTitle>
                  <AlertDescription className="text-amber-700 dark:text-amber-400 space-y-3">
                    <span className="block">
                      Your account has been temporarily suspended because your email address hasn't been verified yet. Check your inbox for a verification email or request a new one below.
                    </span>
                    {resendEmailSent ? (
                      <span className="block font-medium text-amber-800 dark:text-amber-300" data-testid="text-resend-confirmation">
                        Email sent! Check your inbox.
                      </span>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-amber-400 text-amber-800 dark:text-amber-300"
                        disabled={resendMutation.isPending || !email}
                        onClick={() => resendMutation.mutate()}
                        data-testid="button-resend-verification"
                      >
                        {resendMutation.isPending ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          "Resend Verification Email"
                        )}
                      </Button>
                    )}
                    <span className="block text-sm">
                      Questions? Contact us at{" "}
                      <a
                        href="mailto:hello@fillthetrucks.com"
                        className="underline text-amber-800 dark:text-amber-300"
                        data-testid="link-support-email"
                      >
                        hello@fillthetrucks.com
                      </a>
                    </span>
                  </AlertDescription>
                </Alert>
              );
            }
            return (
              <Alert variant="destructive" data-testid="alert-account-suspended">
                <ShieldAlert className="h-4 w-4" />
                <AlertTitle>Account Suspended</AlertTitle>
                <AlertDescription>
                  Your account has been suspended.
                  {suspendedInfo.reason && (
                    <span className="block mt-1" data-testid="text-suspension-reason">
                      Reason: {suspendedInfo.reason}
                    </span>
                  )}
                  <span className="block mt-1">Please contact support for assistance.</span>
                </AlertDescription>
              </Alert>
            );
          })()}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="input-email"
              />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="password">Password</Label>
                <Link 
                  href="/forgot-password" 
                  className="text-sm text-primary hover:underline"
                  data-testid="link-forgot-password"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="input-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 text-base"
              size="lg"
              disabled={loginMutation.isPending}
              data-testid="button-login"
            >
              {loginMutation.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link href="/register" className="text-primary hover:underline font-medium" data-testid="link-sign-up">
                Sign up free
              </Link>
            </p>
          </div>

          <div className="text-center text-xs text-muted-foreground">
            <p>
              By continuing, you agree to our{" "}
              <Link href="/terms-of-use" className="hover:text-foreground underline" data-testid="link-terms">Terms of Service</Link>
              {" "}and{" "}
              <Link href="/privacy-policy" className="hover:text-foreground underline" data-testid="link-privacy">Privacy Policy</Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
